import { getAutomationPieces } from 'api/automation/automationService.js';
import { ConditionOperator } from 'Dictionaries/automation/conditionOperators';
import { escapeHtml } from 'Helpers/htmlSanitizeHelper.js';

const VALUELESS_OPERATORS = [ConditionOperator.EMPTY, ConditionOperator.NOT_EMPTY];

// TODO: the pieces-related methods (initializePiecesList, getUniquePieceNames,
// getPieceNamesForAutomation, extractPieceNames, getAutomationPieces) are
// duplicated in `composables/automation/useAutomationPieces.ts`. When the last
// Options API consumers (AutomationTemplatesSellsy.vue,
// AutomationModalUpdateVersion.vue, AutomationWizardPanel.vue) migrate to
// <script setup>, this mixin can be retired entirely.
export default {
    data() {
        return {
            piecesList: [],
        };
    },
    methods: {
        initializePiecesList(automations) {
            const pieces = this.getUniquePieceNames(automations);
            if (pieces.length === 0) {
                this.piecesList = [];
                return Promise.resolve();
            }
            return this.getAutomationPieces(pieces)
                .then((results) => {
                    this.piecesList = results;
                    for (const automation of automations) {
                        const automationPieceNames = this.getPieceNamesForAutomation(automation);
                        automation.pieces = automationPieceNames
                            .map((pieceName) => {
                                return this.piecesList.find((piece) => {
                                    return piece && piece.originalPieceName === pieceName;
                                });
                            })
                            .filter((piece) => piece);
                    }
                })
                .catch(() => {
                    this.piecesList = [];
                });
        },
        getUniquePieceNames(flows) {
            const pieceNames = new Set();

            for (const flow of flows) {
                const trigger = flow.version?.trigger;
                if (trigger) {
                    this.extractPieceNames(trigger, pieceNames);
                }
            }
            return Array.from(pieceNames);
        },
        getPieceNamesForAutomation(automation) {
            const pieceNames = new Set();
            const trigger = automation.version?.trigger;
            if (trigger) {
                this.extractPieceNames(trigger, pieceNames);
            }
            return Array.from(pieceNames);
        },
        extractPieceNames(action, pieceNames) {
            if (!action || typeof action !== 'object') {
                return;
            }

            if (action.settings?.pieceName) {
                pieceNames.add(action.settings.pieceName);
            }

            if (action.nextAction) {
                this.extractPieceNames(action.nextAction, pieceNames);
            }

            if (Array.isArray(action.children)) {
                for (const child of action.children) {
                    this.extractPieceNames(child, pieceNames);
                }
            }

            if (action.firstLoopAction) {
                this.extractPieceNames(action.firstLoopAction, pieceNames);
            }
        },
        getAutomationPieces(pieceNames) {
            const cleanedPieceNames = pieceNames.map((name) => {
                if (name.startsWith('@activepieces/')) {
                    return name.replace('@activepieces/', '');
                }
                return name;
            });

            return getAutomationPieces(cleanedPieceNames)
                .then((response) => {
                    const piecesData = response.data;
                    const results = [];
                    for (let i = 0; i < pieceNames.length; i++) {
                        const originalPieceName = pieceNames[i];
                        const cleanedPieceName = cleanedPieceNames[i];
                        const pieceData = piecesData[cleanedPieceName];
                        if (pieceData) {
                            results.push({
                                displayName: pieceData.displayName,
                                logoUrl: pieceData.logoUrl,
                                originalPieceName: originalPieceName,
                            });
                        }
                    }

                    return results;
                })
                .catch((error) => {
                    this.errorsHandler.logException(error);
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('common.labels.toast.error'),
                    });
                    return [];
                });
        },
        prepareFormDataForSubmission(effectiveTemplate, template, formData) {
            const templateData = JSON.parse(JSON.stringify(effectiveTemplate.metadata));
            if (template) {
                templateData.template = {};
                templateData.template.id = effectiveTemplate.id;
                templateData.template.title = effectiveTemplate.title;
                templateData.template.description = effectiveTemplate.description;
                templateData.template.version = effectiveTemplate.metadata.template.version;
            }

            if (templateData.wizard_schema) {
                for (const step of templateData.wizard_schema) {
                    if (step.fields) {
                        for (const field of step.fields) {
                            this.updateFieldWithFormData(formData, field);
                        }
                    }
                }
            }

            const originalPropertyDefinitions = (templateData.wizard_conditions?.conditions || [])
                .filter((condition) => condition.scope === 'object.properties')
                .map(({ rank, operator, value, ...base }) => ({ ...base }));

            const configuredConditions = (formData.conditions || []).filter((condition) => {
                if (!condition.id || !condition.operator) {
                    return false;
                }
                if (VALUELESS_OPERATORS.includes(condition.operator)) {
                    return true;
                }
                if (
                    condition.value === null ||
                    condition.value === undefined ||
                    condition.value === '' ||
                    condition.value === '[]'
                ) {
                    return false;
                }
                return true;
            });

            const sanitizedConditions = configuredConditions.map((condition, index) => ({
                ...condition,
                value:
                    condition.value !== null && condition.value !== undefined
                        ? escapeHtml(condition.value)
                        : null,
                rank: index + 1,
            }));

            const configuredPropertyIds = new Set(
                sanitizedConditions
                    .filter((condition) => condition.scope === 'object.properties')
                    .map((condition) => condition.id),
            );
            const unconfiguredProperties = originalPropertyDefinitions.filter(
                (prop) => !configuredPropertyIds.has(prop.id),
            );

            const allConditions = [...sanitizedConditions, ...unconfiguredProperties];

            if (allConditions.length > 0) {
                templateData.wizard_conditions = {
                    ...templateData.wizard_conditions,
                    conditions: allConditions,
                };
            } else if (templateData.wizard_conditions) {
                delete templateData.wizard_conditions.conditions;
            }

            return {
                metadata: templateData,
            };
        },
        updateFieldWithFormData(formData, field) {
            const fieldValue = formData[field.id];
            if (fieldValue !== undefined && fieldValue !== '') {
                field.value = fieldValue;
                this.synchronizeFieldProps(field, fieldValue);
            }
        },
        synchronizeFieldProps(field, fieldValue) {
            switch (field.type) {
                case 'select':
                case 'pipeline_step_opp':
                case 'document_type_status':
                    this.updateSelectFieldProps(field, fieldValue);
                    break;
                case 'date_time':
                    this.updateDateTimeFieldProps(field, fieldValue);
                    break;
                case 'date':
                case 'time':
                case 'text':
                case 'textarea':
                case 'amount':
                case 'percent':
                    this.updateInputFieldProps(field, fieldValue);
                    break;
                case 'priority_task':
                    this.updatePriorityFieldProps(field, fieldValue);
                    break;
                default:
                    break;
            }
        },
        updateSelectFieldProps(field, fieldValue) {
            if (field.props?.select) {
                field.props.select.value = fieldValue;
            }
        },
        updateDateTimeFieldProps(field, fieldValue) {
            const dayValue = this.extractValuePart(fieldValue, 'day');
            const hourValue = this.extractValuePart(fieldValue, 'hour');
            if (field.props?.input && dayValue) {
                field.props.input.value = dayValue;
            }
            if (field.props?.timePicker && hourValue) {
                field.props.timePicker.value = hourValue;
            }
        },
        extractValuePart(value, unit) {
            if (!value || typeof value !== 'string') {
                return '';
            }
            const match = value.match(new RegExp(`\\+\\s*(\\d+(?:\\.\\d+)?)\\s*${unit}`));
            return match ? match[0] : '';
        },
        updateInputFieldProps(field, fieldValue) {
            if (field.props?.input) {
                field.props.input.value = fieldValue;
            }
        },
        updatePriorityFieldProps(field, fieldValue) {
            if (field.props?.iconButton) {
                field.props.iconButton.value = fieldValue;
            }
        },
    },
};
